import axios from 'axios';

// API 基础配置
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080',
  timeout: 30000,
});

// 请求拦截器 - 添加认证 token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 处理错误
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 类型定义
export interface App {
  id: number;
  name: string;
  display_name: string;
  type: 'normal' | 'modpack';
  api_key?: string;
  created_at: string;
}

export interface Version {
  id: number;
  app_id: number;
  version: string;
  platform: string;
  architecture: string;
  file_path: string;
  file_name: string;
  file_size: number;
  checksum: string;
  changelog: string;
  is_default: boolean;
  released_at: string;
  created_at: string;
}

export interface VersionFile {
  id: number;
  version_id: number;
  path: string;
  size: number;
  checksum: string;
  file_path: string;
  created_at: string;
}

export interface FileManifestEntry {
  path: string;
  size: number;
  checksum: string;
}

export interface FileManifestDiff {
  to_download: FileManifestEntry[];
  to_keep: FileManifestEntry[];
  to_skip: FileManifestEntry[];
}

export interface CreateAppRequest {
  name: string;
  display_name: string;
  type: 'normal' | 'modpack';
}

export interface CreateVersionRequest {
  version: string;
  platform: string;
  architecture: string;
  changelog: string;
  is_default: boolean;
}

// API 方法
export const apiMethods = {
  // 应用管理
  getApps: () => api.get<any>('/update/admin/apps'),
  getApp: (id: number) => api.get<any>(`/update/admin/apps/${id}`),
  createApp: (data: CreateAppRequest) => api.post<any>('/update/admin/apps', data),
  updateApp: (id: number, data: Partial<CreateAppRequest>) => api.put<any>(`/update/admin/apps/${id}`, data),
  deleteApp: (id: number) => api.delete(`/update/admin/apps/${id}`),
  regenerateApiKey: (id: number) => api.post<any>(`/update/admin/apps/${id}/api-key`),

  // 版本管理
  getVersions: (appId: number) => api.get<any>(`/update/admin/apps/${appId}/versions`),
  getVersion: (id: number) => api.get<any>(`/update/admin/versions/${id}`),
  createVersion: (appId: number, data: FormData) => api.post<any>(`/update/admin/apps/${appId}/versions`, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  updateVersion: (id: number, data: Partial<Version>) => api.put<any>(`/update/admin/versions/${id}`, data),
  setDefaultVersion: (id: number) => api.put<any>(`/update/admin/versions/${id}/default`),
  deleteVersion: (id: number) => api.delete(`/update/admin/versions/${id}`),

  // 文件清单管理
  getVersionFiles: (versionId: number) => api.get<{ files: FileManifestEntry[] }>(`/update/versions/${versionId}/files`),
  downloadFile: (fileId: number) => api.get(`/update/files/${fileId}`, { responseType: 'blob' }),
};

export default api;
